import React from 'react';

import { Money, formatMoney, isValidMoney, parseMoney } from '@betterment/core';
import { Box, Button, Checkbox, FormikField, Input, Label, Select, Text } from '@betterment/samba-react';
import { FormikValidateAfterSubmit } from '@betterment/shared-components';
import { formatNumberWithDecimals } from '@betterment/shared-components/src/utils/NumbersHelper';
import {
  moneyTradeEstimateCopy,
  sharesTradeEstimateCopy,
} from '@betterment/shared-components/src/utils/tradeEstimateHelpers';
import { Form, FormikHelpers, useFormikContext } from 'formik';

import { BorderedBox } from './BorderedBox';
import { Disclaimer } from './Disclaimer';
import { SaleDetailsRow } from './SaleDetailsRow';
import { SellOrderFormValues, ValidationSchema } from './schema';
import { separatorStyles } from './styles';
import { CoordinatedAccount, OrderUnit } from './types';

import { FormikMoneyInput } from '../../FormikMoneyInput';

interface SellOrderFormLoadedProps {
  accountDisplayName: string;
  assetName: string;
  assetTicker: string;
  coordinatedAccounts: CoordinatedAccount[];
  handleSubmit: (values: SellOrderFormValues, formikHelpers: FormikHelpers<SellOrderFormValues>) => Promise<void>;
  initialFormValues: SellOrderFormValues;
  isTaxCoordinated: boolean;
  pricePerShare: Money;
  clientNames: string;
}

const SellOrderFormContent: React.FC<{
  accountDisplayName: string;
  assetName: string;
  assetTicker: string;
  coordinatedAccounts: CoordinatedAccount[];
  isTaxCoordinated: boolean;
  pricePerShare: Money;
  clientNames: string;
}> = ({
  accountDisplayName,
  assetName,
  assetTicker,
  coordinatedAccounts,
  isTaxCoordinated,
  pricePerShare,
  clientNames,
}) => {
  const { values, setFieldValue, isSubmitting, errors } = useFormikContext<SellOrderFormValues>();
  const [shouldShowSpinner, setShouldShowSpinner] = React.useState(false);

  React.useEffect(() => {
    setShouldShowSpinner(wasShowingSpinner => isSubmitting || (wasShowingSpinner && Object.keys(errors).length === 0));
  }, [isSubmitting, errors]);

  const selectedAccount = coordinatedAccounts.find(account => account.id === values.coordinatedAccountId);
  const displayAmount = selectedAccount?.amount;
  const displayShares = selectedAccount?.shares;

  React.useEffect(() => {
    if (selectedAccount) {
      const heldAmount = values.unit === OrderUnit.Dollars ? selectedAccount.amount : selectedAccount.shares;
      void setFieldValue('heldAmount', heldAmount);
    } else {
      const heldAmount = values.unit === OrderUnit.Dollars ? Money(0) : 0;
      void setFieldValue('heldAmount', heldAmount);
    }
  }, [values.coordinatedAccountId, values.unit, selectedAccount, setFieldValue]);

  const previousUnit = React.useRef(values.unit);
  React.useEffect(() => {
    if (previousUnit.current !== values.unit) {
      void setFieldValue('amount', '');
      previousUnit.current = values.unit;
    }
  }, [values.unit, setFieldValue]);

  React.useEffect(() => {
    if (values.sellAll) {
      void setFieldValue('amount', '');
    }
  }, [values.sellAll, setFieldValue]);

  const tradeEstimateCopy = ({
    unit,
    amount,
    price,
  }: {
    unit: OrderUnit;
    amount: Money | string | number;
    price: Money;
  }) => {
    if (unit === OrderUnit.Dollars) {
      let moneyAmount: Money | undefined;

      if (isValidMoney(amount)) {
        moneyAmount = amount;
      } else if (typeof amount === 'string') {
        moneyAmount = parseMoney(amount) ?? undefined;
      }

      return sharesTradeEstimateCopy(moneyAmount, price);
    }
    return moneyTradeEstimateCopy(Number(amount), price);
  };

  return (
    <Form>
      <Box css={{ display: 'flex', flexDirection: 'column', rowGap: 'space.300' }}>
        <BorderedBox css={{ '& > *': separatorStyles }}>
          <SaleDetailsRow>
            <SaleDetailsRow.Label>Client</SaleDetailsRow.Label>
            <SaleDetailsRow.Value>{clientNames}</SaleDetailsRow.Value>
          </SaleDetailsRow>
          <SaleDetailsRow>
            <SaleDetailsRow.Label>Account</SaleDetailsRow.Label>
            <SaleDetailsRow.Value>{accountDisplayName}</SaleDetailsRow.Value>
          </SaleDetailsRow>

          <SaleDetailsRow>
            <SaleDetailsRow.Label>Asset</SaleDetailsRow.Label>
            <SaleDetailsRow.Value>
              <SaleDetailsRow.Value.Primary>{assetName}</SaleDetailsRow.Value.Primary>
              <SaleDetailsRow.Value.Secondary>{assetTicker}</SaleDetailsRow.Value.Secondary>
            </SaleDetailsRow.Value>
          </SaleDetailsRow>

          {isTaxCoordinated && (
            <Box css={{ paddingY: 'space.300' }}>
              <Label css={{ marginBottom: 'space.100' }}>Select a coordinated account to sell from:</Label>
              <FormikField name="coordinatedAccountId">
                <FormikField.Control>
                  <Select>
                    <Select.Trigger
                      aria-label="coordinated account"
                      aria-description={
                        coordinatedAccounts.length === 0 ? 'No selectable accounts available.' : undefined
                      }
                    >
                      <Select.Value placeholder="Select account" />
                    </Select.Trigger>
                    <Select.Content>
                      {coordinatedAccounts.length === 0 ? (
                        <Select.Item value="no-accounts-available" disabled={true} css={{ color: 'text.weak' }}>
                          No selectable accounts available.
                        </Select.Item>
                      ) : (
                        coordinatedAccounts.map(account => (
                          <Select.Item key={account.id} value={account.id}>
                            {account.name}
                          </Select.Item>
                        ))
                      )}
                    </Select.Content>
                  </Select>
                </FormikField.Control>
                <FormikField.ErrorMessage />
              </FormikField>
            </Box>
          )}

          {selectedAccount && (
            <SaleDetailsRow>
              <SaleDetailsRow.Label id="available-to-sell">Available to sell</SaleDetailsRow.Label>
              <SaleDetailsRow.Value aria-labelledby="available-to-sell">
                <SaleDetailsRow.Value.Primary>
                  {displayAmount ? formatMoney(displayAmount) : '—'}
                </SaleDetailsRow.Value.Primary>
                <SaleDetailsRow.Value.Secondary>
                  {displayShares !== null && displayShares !== undefined
                    ? `${formatNumberWithDecimals(displayShares)} shares`
                    : '—'}
                </SaleDetailsRow.Value.Secondary>
              </SaleDetailsRow.Value>
            </SaleDetailsRow>
          )}
          {!selectedAccount && coordinatedAccounts.length === 0 && (
            <SaleDetailsRow>
              <SaleDetailsRow.Label id="available-to-sell">Available to sell</SaleDetailsRow.Label>
              <SaleDetailsRow.Value>$0.00</SaleDetailsRow.Value>
            </SaleDetailsRow>
          )}
        </BorderedBox>

        <BorderedBox css={{ display: 'flex', flexDirection: 'column', rowGap: 'space.300', paddingY: 'space.300' }}>
          <Box>
            <Box css={{ display: 'flex', columnGap: 'space.100', alignItems: 'flex-end' }}>
              <Box css={{ flex: 1 }}>
                {values.unit === OrderUnit.Shares ? (
                  <FormikField name="amount">
                    <FormikField.Label>Amount to sell</FormikField.Label>
                    <FormikField.Control>
                      <Input type="number" onWheel={e => e.currentTarget.blur()} disabled={values.sellAll} />
                    </FormikField.Control>
                  </FormikField>
                ) : (
                  <FormikMoneyInput name="amount" label="Amount to sell" showError={false} disabled={values.sellAll} />
                )}
              </Box>

              <Box css={{ minWidth: '150px' }}>
                <FormikField name="unit">
                  <FormikField.Control>
                    <Select disabled={values.sellAll}>
                      <Select.Trigger
                        aria-label="unit selection"
                        css={{
                          _disabled: {
                            color: 'text.disabled',
                            bg: 'background.input.disabled',
                            cursor: 'auto',
                          },
                        }}
                      >
                        <Select.Value />
                      </Select.Trigger>
                      <Select.Content>
                        <Select.Item value={OrderUnit.Dollars}>Dollars</Select.Item>
                        <Select.Item value={OrderUnit.Shares}>Shares</Select.Item>
                      </Select.Content>
                    </Select>
                  </FormikField.Control>
                </FormikField>
              </Box>
            </Box>
            <FormikField name="amount">
              <FormikField.ErrorMessage />
            </FormikField>
            <FormikField name="_base">
              <FormikField.ErrorMessage />
            </FormikField>
            {!values.sellAll && (
              <Text css={{ color: 'text.weak' }} variant="text50">
                {pricePerShare.cents > 0 &&
                  tradeEstimateCopy({ unit: values.unit, amount: values.amount, price: pricePerShare })}
              </Text>
            )}
          </Box>
          {displayShares !== undefined && (
            <Box>
              <FormikField name="sellAll">
                <FormikField.Control>
                  <Checkbox>
                    <Checkbox.Control />
                    <Checkbox.Label>Sell all {formatNumberWithDecimals(displayShares)} shares</Checkbox.Label>
                  </Checkbox>
                </FormikField.Control>
              </FormikField>
            </Box>
          )}
          <Disclaimer />
        </BorderedBox>

        <Box css={{ pt: 'space.100' }}>
          <Button variant="solidPrimary" type="submit" loading={shouldShowSpinner}>
            Review order
          </Button>
        </Box>
      </Box>
    </Form>
  );
};

export const SellOrderFormLoaded: React.FC<SellOrderFormLoadedProps> = ({
  accountDisplayName,
  assetName,
  assetTicker,
  coordinatedAccounts,
  handleSubmit,
  initialFormValues,
  isTaxCoordinated,
  pricePerShare,
  clientNames,
}) => {
  return (
    <FormikValidateAfterSubmit<SellOrderFormValues>
      initialValues={initialFormValues}
      validationSchema={ValidationSchema}
      onSubmit={handleSubmit}
    >
      <SellOrderFormContent
        accountDisplayName={accountDisplayName}
        assetName={assetName}
        assetTicker={assetTicker}
        coordinatedAccounts={coordinatedAccounts}
        isTaxCoordinated={isTaxCoordinated}
        pricePerShare={pricePerShare}
        clientNames={clientNames}
      />
    </FormikValidateAfterSubmit>
  );
};
